import { CheckoutRules, type CheckoutRuleDocumentType } from '../interfaces'
import type { ICheckoutRuleResolvedParams } from '../interfaces/tag_manager'
import { fireGTMEvent } from './fireGTMEvent'

/**
 * COM-1528. `commerce-checkout_rule_resolved` has to fire **once per session
 * and rule**, and two producers now compete to be the first terminal writer:
 *
 * - `useCheckoutRuleAutoSettle` on the cart/summary — originally the only one.
 * - `useStartGuidedWalk` (COM-1501) from the catalog, which settles each rule
 *   before the cart/summary hook mounts. Without a shared guard the cart hook
 *   never fires on catalog-started sessions, because its terminal-status
 *   latch returns at the top of the effect.
 *
 * A per-hook `useRef` cannot coordinate across hook instances. A redux slice
 * entry could, but the state is purely analytics-coordination noise with no
 * reader anywhere else. A module-level set keyed on `sessionId:ruleType` is
 * the right scope: it survives across hook instances within one webview
 * session, resets on a session change, and leaves no trace in the store.
 */

const firedPairs = new Set<string>()
let currentSessionId: string | undefined

const pairKey = (sessionId: string, ruleType: CheckoutRuleDocumentType) =>
  `${sessionId}:${ruleType}`

const resetIfSessionChanged = (sessionId: string): void => {
  if (currentSessionId === sessionId) return
  currentSessionId = sessionId
  firedPairs.clear()
}

/**
 * Fires `commerce-checkout_rule_resolved` for `ruleType`, exactly once per
 * `(sessionId, ruleType)` pair for the lifetime of this module. Returns
 * `true` if the event fired, `false` if it was already fired for this pair.
 */
export const fireCheckoutRuleResolved = ({
  ruleType,
  resolution,
  optionCount,
  sessionId,
}: {
  ruleType: CheckoutRuleDocumentType
  resolution: ICheckoutRuleResolvedParams['resolution']
  optionCount: number
  sessionId: string | undefined
}): boolean => {
  if (!sessionId) return false
  resetIfSessionChanged(sessionId)

  const key = pairKey(sessionId, ruleType)
  if (firedPairs.has(key)) return false
  firedPairs.add(key)

  fireGTMEvent('commerce-checkout_rule_resolved', {
    rule_type:
      ruleType === CheckoutRules.DELIVERY_DATES
        ? 'delivery_date'
        : 'payment_method',
    resolution,
    option_count: optionCount,
  })
  return true
}

/** Test-only hatch for the module-level set. */
export const resetFiredCheckoutRuleResolved = (): void => {
  firedPairs.clear()
  currentSessionId = undefined
}

export default fireCheckoutRuleResolved
